import React, { useEffect, useMemo, useState } from "react";
import { toCsv } from "./fiData";
import "./fi-extra.css";

/* Shared building blocks for the admin research lab. Nothing here changes data. */

export const humanize = (token) =>
  token ? String(token).toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "—";

const TONES = {
  READY: "ok",
  OBSERVED_BASELINE: "ok",
  CAPTURED: "ok",
  EXPERIMENTAL_NOT_VALIDATED: "warn",
  STALE_QUOTE_RESEARCH_ONLY: "warn",
  NEEDS_HISTORY: "warn",
  INSUFFICIENT_DATA: "warn",
  RIGHTS_RESTRICTED: "warn",
  COMMERCIAL_RIGHTS_UNVERIFIED: "warn",
  RETROSPECTIVE_REVISION_UNSAFE: "warn",
};

export function StatusBadge({ value, title }) {
  return <span className={`fx-badge ${TONES[value] || "muted"}`} title={title}>{humanize(value)}</span>;
}

export function QuoteAge({ hours, basis }) {
  if (typeof hours !== "number") return <span className="fx-badge muted">Unknown</span>;
  const fresh = hours >= 0 && hours <= 6;
  const note = basis === "database_ingestion_only" ? "Measured from database ingestion: the provider supplied no quote timestamp." : "Measured from the provider's quote timestamp.";
  return <span className={`fx-badge ${fresh ? "ok" : "warn"}`} title={note}>{hours.toFixed(1)}h{basis === "database_ingestion_only" ? " · ingest" : ""}</span>;
}

/** Debounces a value so free-text filters do not fire a request per keystroke. */
export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Click a header to sort; click again to reverse. */
export function useSort(rows, initialKey, accessors, initialDir = "asc") {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });
  const sorted = useMemo(() => {
    const get = accessors[sort.key] || (() => null);
    return [...rows].sort((a, b) => {
      const x = get(a);
      const y = get(b);
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      const result = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? result : -result;
    });
  }, [rows, sort, accessors]);
  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  return { sorted, sort, toggle };
}

export function SortHeader({ label, k, sort, toggle, numeric }) {
  const active = sort.key === k;
  return (
    <th className={numeric ? "fx-num" : undefined} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => toggle(k)} style={{ all: "unset", cursor: "pointer", textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}{active ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );
}

export function Pager({ offset, limit, total, hasMore, onChange }) {
  const from = total ? offset + 1 : 0;
  const to = Math.min(offset + limit, total);
  return (
    <div className="fx-pager">
      <button type="button" className="fx-button" disabled={!offset} onClick={() => onChange(Math.max(0, offset - limit))}>Previous</button>
      <button type="button" className="fx-button" disabled={!hasMore} onClick={() => onChange(offset + limit)}>Next</button>
      <span>Showing {from}–{to} of {total}</span>
    </div>
  );
}

export function downloadCsv(filename, rows, columns) {
  const blob = new Blob([toCsv(rows, columns)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const pct = (value) => (typeof value === "number" ? `${(value * 100).toFixed(1)}%` : "—");

export const LEDGER_COLUMNS = (fixtureName) => [
  { label: "Fixture", value: (r) => fixtureName(r.fixture_id) || r.fixture_id },
  { label: "Fixture id", value: (r) => r.fixture_id },
  { label: "Kickoff UTC", value: (r) => r.kickoff_utc },
  { label: "Competition", value: (r) => r.competition },
  { label: "Market", value: (r) => r.market },
  { label: "Line", value: (r) => r.exact_line },
  { label: "Bookmaker", value: (r) => r.bookmaker },
  { label: "Odds", value: (r) => r.odds },
  { label: "Model", value: (r) => r.model_id },
  { label: "Model version", value: (r) => r.model_version },
  { label: "Probability", value: (r) => r.probability },
  { label: "Raw EV", value: (r) => r.expected_value },
  { label: "Quote age hours (now)", value: (r) => r.quote_age_hours_now },
  { label: "Quote time basis", value: (r) => r.quote_time_basis },
  { label: "Forecast created UTC", value: (r) => r.prediction_created_at },
  { label: "Eligibility", value: (r) => r.eligibility_status },
  { label: "Abstention reason", value: (r) => r.abstention_reason },
];

/**
 * The hypothetical-forecast ledger. Abstentions are shown as abstentions, never
 * as zero probability, and nothing here is a published pick.
 */
export function LedgerTable({ rows = [], fixtureName = () => "" }) {
  if (!rows.length) {
    return <p className="fx-callout">No quote-linked forecasts satisfy these filters. Check coverage and abstentions; no probability has been manufactured.</p>;
  }
  return (
    <div className="fx-scroll">
      <table style={{ minWidth: 880 }}>
        <thead>
          <tr>
            <th>Fixture</th><th>Market</th><th>Book · odds</th><th>Model</th>
            <th className="fx-num">Probability</th><th className="fx-num">Raw EV</th><th>Quote age</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.identity}>
              <td>
                {fixtureName(r.fixture_id) || `Fixture ${r.fixture_id}`}
                <small>{r.competition} · {r.kickoff_utc ? new Date(r.kickoff_utc).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "kickoff unknown"} · #{r.fixture_id}</small>
              </td>
              <td>{r.market}<small>{r.exact_line != null ? `line ${r.exact_line}` : "no line"}</small></td>
              <td>{r.bookmaker}<small>{r.odds}</small></td>
              <td>{r.model_id}<small>{(r.model_version || "").split(":")[0]}</small></td>
              <td className="fx-num">{pct(r.probability)}</td>
              <td className="fx-num">{pct(r.expected_value)}</td>
              <td><QuoteAge hours={r.quote_age_hours_now} basis={r.quote_time_basis} /></td>
              <td>
                <StatusBadge value={r.eligibility_status} />
                {r.abstention_reason && <small>{humanize(r.abstention_reason)}</small>}
                {r.settlement?.unit_profit != null ? <small>Unit P/L {r.settlement.unit_profit}</small> : <small>Ungraded</small>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
